import { EntityUser, UserAnalyticsTab } from './../shared/domain/user.entity';
import { HttpConnectorAdapter } from './../adapters/http-connector-adapter';
import { User } from '../shared/domain';
import { UserFull } from '../models/user';
import { Role, ROLE_TYPE } from '../models/role';

export interface UserIdentifiers {
    account_id: string;
    team_id: string;
    user_id: string;
    auth_token?: string;
    expiry?: number;
}

export interface UserPayloadEntry {
    email: string;
    first_name: string;
    last_name: string;
    team: string;
    password: string;
}

export interface BulkUserPayload {
    account_id: string;
    user_entries: UserPayloadEntry[];
}

export default class UserProvider {
    private endpointAdmin: string;

    constructor() {
        this.endpointAdmin = `${process.env.REACT_APP_ADMIN_TOOL_API}user/`;
    }

    init = () => {
        return HttpConnectorAdapter;
    };

    getUserIdentifiers = (userEmail: string) => {
        return this.init()
            .getWithCredentials(`${this.endpointAdmin}${userEmail}/email`)
            .then(response => {
                const { results } = response.data;

                const user = results.users[0] as EntityUser;
                const { user_id, teams } = user;

                return { user_id, teams };
            });
    };

    fetchUser = (userEmail: string) => {
        return this.init()
            .getWithCredentials(`${this.endpointAdmin}${userEmail}/email`)
            .then(response => {
                const { results } = response.data;
                const users: EntityUser[] = results.users as EntityUser[];
                return users.map((user: EntityUser) => {
                    return User.create(user);
                });
            });
    };

    fetchUserById = (userId: string) => {
        return this.init()
            .getWithCredentials(`${this.endpointAdmin}${userId}`)
            .then(response => {
                const user = response.data as User;
                const statusCode = response.status;

                return { status: statusCode, user };
            });
    };

    fetchFullUserById = (userId: string) => {
        return this.init()
            .getWithCredentials(`${this.endpointAdmin}${userId}`)
            .then(response => {
                const user = response.data as UserFull;
                const statusCode = response.status;

                return { status: statusCode, user };
            });
    };

    updateUserTabs = (userId: string, userTabs: UserAnalyticsTab[]) => {
        const payload = { tabs: userTabs };
        const headers = { 'Content-Type': 'application/json' };
        return this.init()
            .putWithCredentials(
                `${this.endpointAdmin}${userId}/tab`,
                payload,
                headers
            )
            .then(response => {
                const { valid } = response.data;

                if (!valid) {
                    throw {
                        error: "Error updating user's tabs",
                        errorMessage: 'Server returned empty response.'
                    };
                }

                return valid;
            });
    };

    addUserToTeam = (userId: string, teamId: string) => {
        return this.init()
            .putWithCredentials(
                `${this.endpointAdmin}${userId}/teams/${teamId}`
            )
            .then(response => {
                const { results } = response.data;
                const user: EntityUser = results.user as EntityUser;
                return User.create(user);
            });
    };

    removeUserFromTeam = (userId: string, teamId: string) => {
        return this.init()
            .deleteWithCredentials(
                `${this.endpointAdmin}${userId}/teams/${teamId}`
            )
            .then(response => {
                const { results } = response.data;
                const user: EntityUser = results.user as EntityUser;
                return User.create(user);
            });
    };

    removeUserFromRole = (userId: string, roleId: string, roleType: ROLE_TYPE = "scheduling") => {
        return this.init()
            .deleteWithCredentials(
                `${this.endpointAdmin}${userId}/scheduling_role/${roleId}?role_type=${roleType}`
            )
            .then(response => {
                const { user_data, user_roles } = response.data;
                return {
                    user: user_data as UserFull,
                    roles: user_roles as Role[]
                };
            });
    };

    addUserToRole = (userId: string, roleId: string, roleType: ROLE_TYPE = "scheduling") => {
        return this.init()
            .putWithCredentials(
                `${this.endpointAdmin}${userId}/scheduling_role/${roleId}?role_type=${roleType}`
            )
            .then(response => {
                const { user_data, user_roles } = response.data;
                return {
                    user: user_data as UserFull,
                    roles: user_roles as Role[]
                };
            });
    };

    resetUserPassword = (userId: string, userEmail: string, password: string, passwordConfirmation: string) => {
        const resetPayload = {
            userId,
            userEmail,
            password,
            "password_confirmation":  passwordConfirmation
        };
        const headers = { 'Content-Type': 'application/json' };
        return this.init().postWithCredentials(`${this.endpointAdmin}${userId}/password_reset`, JSON.stringify(resetPayload), headers).then(response => {
            const statusCode = response.status;

            return { status: statusCode };
        });
    };
}
